import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';
import { clearAllAuthLocal, getStoredUserId } from '../utils/auth';
import './UserNavbar.css';

function UserNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [navCollapsed, setNavCollapsed] = useState(true); // Navbar toggle state
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);

  useEffect(() => {
    fetchUser();
  }, []); // Once on mount — avoid refetch on every route change

  // Close dropdown and navbar when route changes
  useEffect(() => {
    setDropdownOpen(false);
    setNotificationDropdownOpen(false);
    setNavCollapsed(true);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationDropdownOpen(false);
      }
    };

    if (dropdownOpen || notificationDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, notificationDropdownOpen]);

  const fetchUser = async () => {
    try {
      const storedId = getStoredUserId();
      let userId = storedId;
      let sessionData = null;

      if (!userId) {
        const res = await api.get('user/session-check/');
        if (!res.data.authenticated) {
          setUser(null);
          setUserProfile(null);
          return;
        }
        sessionData = res.data;
        userId = res.data.user_id;
      } else {
        sessionData = { authenticated: true, user_id: userId };
      }

      setUser(sessionData);

      try {
        const profileRes = await api.get(`user/profile/${userId}/`);
        setUserProfile(profileRes.data);
        if (!sessionData.email && profileRes.data?.email) {
          setUser((prev) => ({ ...prev, email: profileRes.data.email, name: profileRes.data.name }));
        }
      } catch (profileErr) {
        console.error("Failed to fetch user profile:", profileErr);
        setUserProfile(null);
      }
      fetchNotifications();
    } catch (err) {
      console.error("Navbar session check failed:", err);
      setUser(null);
      setUserProfile(null);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get('notification/user/?limit=5');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await api.post(`notification/user/mark-read/${notificationId}/`);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await api.post('notification/user/mark-all-read/');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'APPOINTMENT_ACCEPTED':
        return 'bi-check-circle-fill text-success';
      case 'APPOINTMENT_REJECTED':
        return 'bi-x-circle-fill text-danger';
      case 'APPOINTMENT_RESCHEDULED':
        return 'bi-calendar-event text-warning';
      case 'APPOINTMENT_CANCELLED':
        return 'bi-calendar-x text-secondary';
      case 'PAYMENT_SUCCESS':
        return 'bi-credit-card-fill text-success';
      default:
        return 'bi-bell-fill text-primary';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getImageUrl = (image) => {
    if (!image) return null;

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }

    if (image.startsWith('/media/')) {
      return `${apiUrl}${image}`;
    }

    return `${apiUrl}/media/${image}`;
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleNavigate = (path) => {
    setDropdownOpen(false);
    setNavCollapsed(true); // Close navbar on navigation
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      // Call logout API
      await api.post('user/logout/');
    } catch (error) {
      // Even if API call fails, proceed with local cleanup
      console.error('Logout API error:', error);
    } finally {
      // Always clear local storage and state
      clearAllAuthLocal();
      setUser(null);
      setDropdownOpen(false);
      setNavCollapsed(true);
      // Navigate to login page
      navigate('/user/login', { replace: true });
    }
  };

  const isActive = (path) => location.pathname === path ? 'active fw-bold' : '';

  return (
    <nav className="navbar navbar-expand-lg navbar-dark bg-primary px-4 shadow-sm sticky-top">
      <div className="container-fluid">
        <span
          className="navbar-brand d-flex align-items-center fw-bold fs-4"
          style={{ cursor: 'pointer', letterSpacing: '1px' }}
          onClick={() => handleNavigate('/user/dashboard')}
        >
          <i className="bi bi-heart-pulse-fill me-2 text-white"></i>
          FITNOVA
        </span>

        <button
          className="navbar-toggler border-0"
          type="button"
          onClick={() => setNavCollapsed(!navCollapsed)}
          aria-controls="userNavbar"
          aria-expanded={!navCollapsed}
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className={`collapse navbar-collapse ${!navCollapsed ? 'show' : ''}`} id="userNavbar">
          <ul className="navbar-nav me-auto mb-2 mb-lg-0 ms-lg-4">
            <li className="nav-item">
              <span
                className={`nav-link ${isActive('/user/dashboard')}`}
                style={{ cursor: 'pointer' }}
                onClick={() => handleNavigate('/user/dashboard')}
              >
                Dashboard
              </span>
            </li>
            <li className="nav-item">
              <span
                className={`nav-link ${isActive('/user/doctor_list')}`}
                style={{ cursor: 'pointer' }}
                onClick={() => handleNavigate('/user/doctor_list')}
              >
                Find Doctors
              </span>
            </li>
            <li className="nav-item">
              <span
                className={`nav-link ${isActive('/user/feed')}`}
                style={{ cursor: 'pointer' }}
                onClick={() => handleNavigate('/user/feed')}
              >
                <i className="bi bi-heart-pulse me-1"></i>Health Feed
              </span>
            </li>
            <li className="nav-item">
              <span
                className={`nav-link ${isActive('/user/bookings')}`}
                style={{ cursor: 'pointer' }}
                onClick={() => handleNavigate('/user/bookings')}
              >
                My Bookings
              </span>
            </li>
            <li className="nav-item">
              <span
                className={`nav-link ${isActive('/user/health-records')}`}
                style={{ cursor: 'pointer' }}
                onClick={() => handleNavigate('/user/health-records')}
              >
                <i className="bi bi-file-medical me-1" />
                Health Records
              </span>
            </li>
            <li className="nav-item">
              <span
                className={`nav-link ${isActive('/user/favorites')}`}
                style={{ cursor: 'pointer' }}
                onClick={() => handleNavigate('/user/favorites')}
              >
                Favorites
              </span>
            </li>
          </ul>

          <div className="d-flex align-items-center gap-3">
            {/* Notification Bell */}
            {user && (
              <button
                className="btn p-0 position-relative text-white"
                type="button"
                onClick={() => navigate('/user/notifications')}
                style={{ background: 'transparent', border: 'none' }}
                title="View Notifications"
              >
                <i className="bi bi-bell fs-5"></i>
                {unreadCount > 0 && (
                  <span
                    className="position-absolute translate-middle badge rounded-pill bg-danger"
                    style={{ top: '5px', left: '100%', fontSize: '0.65rem' }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            )}

            {user ? (
              <div className="dropdown" ref={dropdownRef}>
                <button
                  className="btn p-0 d-flex align-items-center text-white shadow-none"
                  type="button"
                  id="userDropdown"
                  onClick={toggleDropdown}
                  aria-expanded={dropdownOpen}
                  style={{ background: 'transparent', border: 'none', gap: '0.5rem' }}
                >
                  {userProfile?.image ? (
                    <img
                      src={getImageUrl(userProfile.image)}
                      alt={user.name || 'User'}
                      className="navbar-user-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const placeholder = e.target.nextElementSibling;
                        if (placeholder) placeholder.style.display = 'inline-block';
                      }}
                    />
                  ) : null}
                  <i
                    className="bi bi-person-circle fs-5 text-white navbar-user-icon"
                    style={{ display: userProfile?.image ? 'none' : 'inline-block' }}
                  ></i>
                </button>

                <ul
                  className={`dropdown-menu dropdown-menu-end user-profile-dropdown shadow border-0 mt-2 ${dropdownOpen ? 'show' : ''}`}
                  aria-labelledby="userDropdown"
                >
                  <li className="px-3 py-3 border-bottom user-info-header">
                    <div className="d-flex flex-column align-items-center text-center">
                      {userProfile?.image ? (
                        <img
                          src={getImageUrl(userProfile.image)}
                          alt={user.name || 'User'}
                          className="user-profile-image mb-2"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const placeholder = e.target.nextElementSibling;
                            if (placeholder) placeholder.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className="user-avatar-placeholder mb-2"
                        style={{ display: userProfile?.image ? 'none' : 'flex' }}
                      >
                        <i className="bi bi-person-circle"></i>
                      </div>
                      <div className="w-100">
                        <p className="mb-0 fw-bold small text-truncate">{user.email}</p>
                      </div>
                    </div>
                  </li>

                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center justify-content-between"
                      onClick={() => handleNavigate('/user/profile')}
                    >
                      <span className="d-flex align-items-center">
                        <i className="bi bi-person me-2"></i> My Profile
                      </span>
                      <i className="bi bi-chevron-right text-muted"></i>
                    </button>
                  </li>

                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center justify-content-between"
                      onClick={() => handleNavigate('/user/settings')}
                    >
                      <span className="d-flex align-items-center">
                        <i className="bi bi-gear me-2"></i> Settings
                      </span>
                      <i className="bi bi-chevron-right text-muted"></i>
                    </button>
                  </li>

                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center justify-content-between"
                      onClick={() => handleNavigate('/user/privacy')}
                    >
                      <span className="d-flex align-items-center">
                        <i className="bi bi-shield-lock me-2"></i> Privacy and Policy
                      </span>
                      <i className="bi bi-chevron-right text-muted"></i>
                    </button>
                  </li>

                  {/* <li>
                    <button
                      className="dropdown-item d-flex align-items-center justify-content-between"
                      onClick={() => handleNavigate('/user/policy')}
                    >
                      <span className="d-flex align-items-center">
                        <i className="bi bi-file-text me-2"></i> Policy
                      </span>
                      <i className="bi bi-chevron-right text-muted"></i>
                    </button>
                  </li> */}

                  <li><hr className="dropdown-divider" /></li>

                  <li className="px-2 pb-2">
                    <button
                      className="dropdown-item d-flex align-items-center justify-content-center text-white logout-item"
                      onClick={handleLogout}
                      style={{ borderRadius: '6px', padding: '0.4rem 0.8rem', fontSize: '0.875rem' }}
                    >
                      <i className="bi bi-box-arrow-right me-1"></i> Logout
                    </button>
                  </li>
                </ul>
              </div>
            ) : (
              <button
                className="btn btn-outline-light rounded-pill px-4"
                onClick={() => navigate('/user/login')}
              >
                Login
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default UserNavbar;
